import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import * as Icons from "lucide-react";
import { ArrowRight, Phone, CheckCircle2, ChevronRight } from "lucide-react";
import { SITE, SERVICES } from "../lib/site";
import { SERVICE_CONTENT } from "../lib/serviceContent";
import { ARTICLES, ARTICLE_SLUGS } from "../lib/articleContent";
import { CITY_CONTENT, CITY_SLUGS } from "../lib/cityContent";
import QuoteForm from "../components/QuoteForm";

const Icon = ({ name, className }) => {
  const I = Icons[name] || Icons.Wrench;
  return <I className={className} />;
};

const BASE_URL = "https://alliancecraneservice.com";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = useMemo(() => SERVICES.find((s) => s.slug === slug), [slug]);
  const content = SERVICE_CONTENT[slug];

  if (!service || !content) {
    return <Navigate to="/services" replace />;
  }

  const related = (content.related || [])
    .map((rSlug) => ({ ...SERVICES.find((s) => s.slug === rSlug), content: SERVICE_CONTENT[rSlug] }))
    .filter((r) => r && r.slug);

  // Topic-cluster interlink: articles that reference this service
  const guides = ARTICLE_SLUGS
    .map((s) => ARTICLES[s])
    .filter((a) => (a.relatedServices || []).includes(slug))
    .slice(0, 3);

  const canonical = `${BASE_URL}/services/${slug}`;

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    serviceType: service.title,
    description: content.metaDescription,
    provider: {
      "@type": "LocalBusiness",
      "@id": `${BASE_URL}/#business`,
      name: SITE.name,
      telephone: SITE.phoneE164,
      email: SITE.email,
      address: {
        "@type": "PostalAddress",
        addressLocality: "DeLand",
        addressRegion: "FL",
        addressCountry: "US",
      },
    },
    areaServed: [
      "DeLand FL", "Orlando FL", "Daytona Beach FL",
      "Jacksonville FL", "Titusville FL", "Central Florida",
    ],
    url: canonical,
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Services", item: `${BASE_URL}/services` },
      { "@type": "ListItem", position: 3, name: service.title, item: canonical },
    ],
  };

  const faqLd = content.faqs && content.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: content.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null;

  return (
    <>
      <Helmet>
        <title>{`${content.metaTitle} | ${SITE.shortName}`}</title>
        <meta name="description" content={content.metaDescription} />
        <meta name="keywords" content={content.keywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={content.metaTitle} />
        <meta property="og:description" content={content.metaDescription} />
        <meta property="og:image" content={content.heroImage} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(serviceLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        {faqLd && <script type="application/ld+json">{JSON.stringify(faqLd)}</script>}
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[var(--ac-border)]">
        <div className="absolute inset-0">
          <img src={content.heroImage} alt={content.h1} className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 pt-12 md:pt-24 pb-16 md:pb-28">
          {/* Breadcrumbs */}
          <nav
            data-testid="breadcrumbs"
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 md:gap-2 text-[11px] md:text-xs uppercase tracking-[0.14em] md:tracking-[0.18em] font-bold text-zinc-400 mb-6 md:mb-8 flex-wrap"
          >
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/services" className="hover:text-white">Services</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[var(--ac-red)]">{service.title}</span>
          </nav>

          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 border border-[var(--ac-border)] flex items-center justify-center bg-black/70">
              <Icon name={service.icon} className="w-5 h-5 text-[var(--ac-red)]" />
            </div>
            <span className="overline">Service</span>
          </div>

          <h1
            data-testid="service-h1"
            className="font-display text-4xl sm:text-5xl lg:text-6xl max-w-4xl leading-[0.95]"
          >
            {content.h1}
          </h1>

          <p className="mt-5 text-base md:text-lg text-zinc-300 max-w-3xl leading-relaxed">{content.intro}</p>

          <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
            <Link to="/contact" className="btn-primary justify-center" data-testid="service-detail-cta-quote">
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${SITE.phoneE164}`} className="btn-ghost justify-center" data-testid="service-detail-cta-call">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
          </div>
        </div>
      </section>

      {/* BODY + APPLICATIONS */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-7">
            <div className="overline mb-3">Overview</div>
            <h2 className="font-display text-3xl md:text-4xl mb-6 leading-tight">
              Central Florida contractors trust us with the pick.
            </h2>
            {content.body.map((p, i) => (
              <p key={i} className="text-zinc-300 leading-relaxed mb-5">{p}</p>
            ))}

            {content.applications && content.applications.length > 0 && (
              <div className="mt-10">
                <div className="overline mb-4">Common Applications</div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {content.applications.map((a) => (
                    <li key={a} className="flex items-start gap-3 text-zinc-200">
                      <CheckCircle2 className="w-5 h-5 text-[var(--ac-red)] mt-0.5 shrink-0" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <aside className="md:col-span-5">
            <div className="surface p-6 md:p-7">
              <div className="overline mb-4">Why Alliance</div>
              <div className="space-y-5">
                {content.benefits.map((b) => (
                  <div key={b.title} className="border-l-2 border-[var(--ac-red)] pl-4">
                    <div className="font-display text-lg leading-tight">{b.title}</div>
                    <div className="text-sm text-zinc-400 mt-1 leading-relaxed">{b.body}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="surface p-6 md:p-7 mt-5">
              <div className="overline mb-3">Coverage</div>
              <p className="text-zinc-300 text-sm mb-4 leading-relaxed">
                Based in DeLand — serving Orlando, Daytona Beach, Jacksonville, Titusville and all of Central Florida.
              </p>
              <a
                href={`tel:${SITE.phoneE164}`}
                className="btn-primary w-full justify-center"
                data-testid="service-detail-side-call"
              >
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
              <div className="mt-3 text-xs text-zinc-500 font-mono-data uppercase tracking-wider">
                {SITE.hours} · {SITE.emergencyHours}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      {content.faqs && content.faqs.length > 0 && (
        <section className="py-16 md:py-24 bg-[var(--ac-bg-2)] border-y border-[var(--ac-border)]">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="overline mb-4">Questions</div>
              <h2 className="font-display text-4xl md:text-5xl leading-[0.95]">
                Frequently<br /> asked.
              </h2>
            </div>
            <div className="md:col-span-8 border-t border-[var(--ac-border)]">
              {content.faqs.map((f, i) => (
                <details
                  key={i}
                  data-testid={`faq-${i}`}
                  className="group border-b border-[var(--ac-border)] py-5"
                >
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                    <span className="font-display text-lg md:text-xl leading-snug">{f.q}</span>
                    <ChevronRight className="w-5 h-5 shrink-0 text-[var(--ac-red)] mt-1 transition-transform group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-zinc-400 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED */}
      {related.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="overline mb-3">Related</div>
                <h2 className="font-display text-3xl md:text-4xl">Also under our fleet.</h2>
              </div>
              <Link to="/services" className="btn-ghost" data-testid="related-view-all">
                All Services <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-[var(--ac-border)]">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  data-testid={`related-service-${r.slug}`}
                  className="hover-card-border border-b border-r border-[var(--ac-border)] p-7 group bg-[var(--ac-bg)]"
                >
                  <div className="w-11 h-11 border border-[var(--ac-border)] flex items-center justify-center bg-black mb-6">
                    <Icon name={r.icon} className="w-5 h-5 text-[var(--ac-red)]" />
                  </div>
                  <h3 className="font-display text-2xl leading-tight">{r.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400">{r.short}</p>
                  <div className="mt-5 flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-[var(--ac-red)]">
                    Learn more <ChevronRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AVAILABLE IN CITIES (service x city interlink) */}
      <section className="py-14 md:py-20 border-t border-[var(--ac-border)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="overline mb-3">Coverage</div>
          <h2 className="font-display text-3xl md:text-4xl mb-8">
            {service.title} across Central Florida.
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 border-t border-l border-[var(--ac-border)]">
            {CITY_SLUGS.map((citySlug) => (
              <Link
                key={citySlug}
                to={`/services/${slug}/${citySlug}`}
                data-testid={`service-city-link-${citySlug}`}
                className="hover-card-border border-b border-r border-[var(--ac-border)] px-4 py-3.5 flex items-center justify-between group bg-[var(--ac-bg)]"
              >
                <span className="font-display text-base md:text-lg">{CITY_CONTENT[citySlug].name}</span>
                <ChevronRight className="w-4 h-4 text-[var(--ac-red)] opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HELPFUL GUIDES (topic cluster interlink) */}
      {guides.length > 0 && (
        <section className="py-14 md:py-20 bg-[var(--ac-bg-2)] border-t border-[var(--ac-border)]">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="overline mb-3">Helpful Guides</div>
                <h2 className="font-display text-3xl md:text-4xl">Plan your lift.</h2>
              </div>
              <Link to="/resources" className="btn-ghost" data-testid="service-all-resources">
                All Resources <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-[var(--ac-border)]">
              {guides.map((g) => (
                <Link
                  key={g.slug}
                  to={`/resources/${g.slug}`}
                  data-testid={`service-guide-${g.slug}`}
                  className="hover-card-border border-b border-r border-[var(--ac-border)] p-7 group bg-[var(--ac-bg)]"
                >
                  <div className="kbd-badge mb-4">{g.category}</div>
                  <h3 className="font-display text-xl leading-tight group-hover:text-[var(--ac-red)] transition-colors">{g.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400">{g.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* QUOTE CTA */}
      <section className="py-16 md:py-24 bg-black border-t border-[var(--ac-border)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-3 text-[var(--ac-red)]">Get Started</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95]">
              Ready to schedule<br /> your {service.title.toLowerCase()}?
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed max-w-md">
              Send us the details and we will get back with availability and pricing.
              For emergency lifts call our 24/7 line.
            </p>
            <a
              href={`tel:${SITE.phoneE164}`}
              className="btn-primary mt-6"
              data-testid="service-detail-bottom-call"
            >
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
          </div>
          <div className="lg:col-span-7">
            <QuoteForm />
          </div>
        </div>
      </section>
    </>
  );
}
